// docs/work_log v1209 — 안전한 Date 포매팅 헬퍼.
//
// 백엔드는 JSON 직렬화 시 Date 필드를 ISO 문자열로 내보내지만,
// 일부 axios interceptor / 백엔드 serializer 가 Date 객체를 그대로 응답에
// 포함하는 케이스가 있음. 그래서 여기 헬퍼들은 문자열·Date·빈값을 모두 받는다.
//
// 추가수정 2차(26-07-27) §2 — 한국시간(KST) 표시 포맷터는 @bhops/shared 가 정본.
// 콘솔 표시층이 `lib/format-date` 한 곳에서 가져다 쓸 수 있도록 **re-export 만**
// 한다(로컬 재구현 금지 — +9h 시프트 구현이 리포에 이미 3벌 있었다).
//
// ─────────────────────────────────────────────────────────────
// 한국시간 통일 5차(26-08-14) — 이 파일의 정리. **어느 헬퍼를 쓰나**
// ─────────────────────────────────────────────────────────────
//
//   · 사람이 읽는 날짜(화면 라벨)      → kstYmd(v) ?? '–'        "2026.08.14"
//   · 하이픈이 규약인 값               → kstYmdHyphen(v)         "2026-08-14"
//     (<input type="date"> · 파일명 · **서버 KST 키와 문자열로 맞물리는 달력 키**)
//   · <input type="date"> 프리필       → toYmdInput(v)           "" 폴백
//   · 폼 입력값 → 서버 ISO            → ymdToIso(ymd)
//   · **브라우저 로컬** 벽시계 날짜    → toLocalYmd(v)
//
// ⚠ 종전에 있던 `toYmd`(= ISO 를 **UTC 로 자르기**)는 **삭제했다.**
//   근거(실측): 이 저장소가 날짜-only 를 저장하는 관례는 `${ymd}T00:00:00.000Z`
//   (UTC 자정)인데, UTC 자정을 KST 로 읽으면 같은 날 09:00 → **같은 날짜**가 나온다.
//   즉 날짜-only 값에서는 toYmd 와 kstYmd 의 답이 항상 같고, 값이 **진짜 순간**
//   (`new Date()` 로 찍힌 것)이거나 옛 `ymdToIso`(한국 자정 = 전날 15:00Z)로 저장된
//   행일 때만 답이 갈리는데 그 두 경우는 **KST 가 정답**이다. 그러므로 toYmd 가
//   kstYmd 보다 나은 자리는 하나도 없었다 — 이름을 가르는 것보다 **없애는 것**이
//   맞다(같은 뜻의 이름이 둘이면 다음 사람이 반드시 틀린 쪽을 집는다).
//
// ⚠ `toLocalYmd`(브라우저 로컬)는 그대로 둔다 — 캘린더 셀과 이벤트를 **둘 다**
//   클라이언트에서 키잉하는 화면(InstallCalendarPage)의 정답이다(round10 B2).
//   서버가 이미 KST 키를 만들어 보내는 화면(대시보드 3종 캘린더)은 그 키와
//   문자열로 맞물려야 하므로 `kstYmdHyphen` 이 정답이다 — 자리마다 다르다.
export { kstYmd, kstYmdHyphen, kstDateTime, kstTodayYmd, KST_OFFSET_MS } from '@bhops/shared';
export type { KstInput, KstDateTimeOptions } from '@bhops/shared';

import { kstTodayYmd, kstYmdHyphen } from '@bhops/shared';

/**
 * HTML `<input type="date">` 의 value 로 사용할 수 있는 "YYYY-MM-DD" 문자열.
 * null/undefined/Invalid 모두 빈 문자열 반환 (controlled input 안전).
 *
 * 한국시간 통일 5차(26-08-14) — 기준을 UTC 자르기에서 **KST** 로 옮겼다.
 * 이 헬퍼가 채우는 칸은 운영자가 곧바로 저장 버튼을 눌러 되돌려 보내는 자리라
 * "화면에 보이는 날짜"와 "저장될 날짜"가 어긋나면 조용히 하루가 밀린다.
 * 계약일처럼 옛 `ymdToIso`(한국 자정 = 전날 15:00Z)로 저장된 행이 섞여 있는
 * 컬럼에서 실제로 밀리고 있었다(같은 정정을 계약 편집 모달이 이미
 * `contract-edit-validation.ts` 의 `isoToDate` 로 먼저 했다 — 그 선례를 따른다).
 */
export function toYmdInput(v: string | Date | null | undefined): string {
  return kstYmdHyphen(v) ?? '';
}

/**
 * Date/ISO → **브라우저 로컬 타임존** 기준 "YYYY-MM-DD".
 *
 * 로컬 자정으로 만든 Date(예: `new Date(2026, 4, 24)`)를 UTC 로 자르면 KST(UTC+9)
 * 에서 전날(2026-05-23)이 된다. 캘린더 셀 키·이벤트 날짜 매칭·공휴일 판정처럼
 * "그 달력 칸이 가리키는 날짜"가 필요하고 **셀과 이벤트를 둘 다 이 헬퍼로 키잉**
 * 하는 곳은 로컬 기준이어야 셀이 하루씩 밀리지 않는다(round10 B2).
 * `ymdToIso`(로컬자정) 저장분과 reschedule(KST 09:00) 저장분이 같은 칸에 맞는다.
 *
 * ⚠ 서버가 KST 키(`dashboard.service.ts` 의 kstDayKey)를 **이미 만들어 보내는**
 *   화면에서는 이 헬퍼가 아니라 `kstYmdHyphen` 이 정답이다. 브라우저가 한국에
 *   있으면 둘의 답이 같아 로컬에서는 차이가 안 보이지만, 기준이 다른 두 값을
 *   문자열로 맞물리게 두면 다음 사람이 그 사실을 알 길이 없다.
 */
export function toLocalYmd(v: string | Date | null | undefined, fallback = ''): string {
  if (v == null) return fallback;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return fallback;
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * "YYYY-MM-DD" 폼 입력값을 백엔드용 ISO 문자열로 안전 변환.
 *
 * 문제: `new Date('YYYY-MM-DD').toISOString()` 은 입력을 UTC 자정으로 파싱하므로
 * UTC-서쪽 타임존(KST 는 UTC+9 라 안전하지만 미국/유럽 서쪽 타임존에서는 위험)에서
 * 운영자가 의도한 날짜와 다른 캘린더 날짜로 직렬화될 수 있음.
 *
 * 해결: "YYYY-MM-DD" 를 로컬 자정으로 Date 객체를 만든 뒤 ISO 로 변환.
 * 결과 ISO 는 UTC 기준 시각이 달라 보여도 운영자가 의도한 "그 순간"을 가리킴.
 *
 * 백엔드 DTO 는 `@IsDateString()` 으로 ISO + YYYY-MM-DD 모두 허용하지만,
 * Prisma Date 컬럼에 매핑되므로 안전한 풀 ISO 를 보내는 것이 권장 패턴.
 */
export function ymdToIso(ymd: string): string {
  // YYYY-MM-DD 가 아닌 입력은 그대로 통과 (이미 ISO 거나, 빈 문자열인 경우).
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/** 월 격자 커서 — `month` 는 0-based(= Date.getMonth 와 같은 규약). */
export interface MonthCursor {
  year: number;
  /** 0 = 1월. */
  month: number;
}

/**
 * "어느 달을 그릴 것인가"를 **KST 기준**으로 정한다.
 *
 * 한국시간 통일 5차(26-08-14) — 대시보드 캘린더 3종이 `new Date().getUTCMonth()`
 * 로 커서를 잡고 있었다. 한국 1월 1일 오전 0~9시에 열면 UTC 로는 아직 12월 31일
 * 이라 **작년 12월**이 그려지고 [오늘] 버튼도 거기로 점프한다. 격자 커서와
 * '오늘' 테두리를 각각 고치면 또 갈리므로 두 곳이 같은 함수에서 나오게 둔다.
 *
 * @param ymdOrIso 지정하면 그 날짜가 속한 달(문자열/ISO 모두 KST 로 읽는다).
 *                 없으면 KST 오늘이 속한 달.
 */
export function kstMonthCursor(ymdOrIso?: string | Date | null): MonthCursor {
  const ymd = (ymdOrIso ? kstYmdHyphen(ymdOrIso) : null) ?? kstTodayYmd();
  const [y, m] = ymd.split('-').map(Number);
  return { year: y, month: m - 1 };
}
